import { useState, useEffect, useRef, type RefObject } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GoogleReview {
  authorName: string;
  rating: number;
  text: string;
  time: number;
  relativeTime: string;
  profilePhoto: string;
}

interface GoogleReviewData {
  rating: number;
  reviewCount: number;
  reviews: GoogleReview[];
}

/**
 * Defers the Supabase google-reviews fetch until a sentinel element
 * enters the viewport (IntersectionObserver). This keeps the API call
 * completely out of the critical rendering path.
 */
export const useGoogleReviews = (sentinelRef: RefObject<HTMLElement | null>) => {
  const [data, setData] = useState<GoogleReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || fetched.current) return;

    let cancelled = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !fetched.current) {
          fetched.current = true;
          observer.disconnect();

          (async () => {
            try {
              const { data: result, error } = await supabase.functions.invoke(
                "google-reviews"
              );
              if (!error && result && !cancelled) {
                setData(result as GoogleReviewData);
              }
            } catch (e) {
              console.error("Failed to fetch Google reviews:", e);
            } finally {
              if (!cancelled) setLoading(false);
            }
          })();
        }
      },
      { rootMargin: "200px" } // start fetching 200px before visible
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [sentinelRef]);

  return { data, loading };
};
